import { type Hardfork, selectHardforkForTimestamp } from '@endure/chains';

const getBlockTimeByTimestamp = ({
  timestamp,
  hardforks = [],
}: { timestamp: number; hardforks: Hardfork[] }): Hardfork | undefined =>
  selectHardforkForTimestamp({ hardforks, timestamp });

export const getEstimatedDateByBlockHeight = ({
  targetBlockHeight,
  currentBlockHeight,
  hardforks = [],
}: {
  targetBlockHeight: number;
  currentBlockHeight: number;
  hardforks: Hardfork[];
}) => {
  const now = new Date().getTime();
  const totalBlockDiff = currentBlockHeight - targetBlockHeight; // negative: target is in the future
  const nowBlockTime = getBlockTimeByTimestamp({ timestamp: now, hardforks });

  if (!nowBlockTime) {
    return undefined;
  }

  const estimatedTargetTime = now - nowBlockTime.blockTimeMs * totalBlockDiff;
  const estimatedBlockTime = getBlockTimeByTimestamp({
    timestamp: estimatedTargetTime,
    hardforks,
  });

  if (!estimatedBlockTime || estimatedBlockTime.blockTimeMs === nowBlockTime.blockTimeMs) {
    return new Date(estimatedTargetTime);
  }

  const amendedBlockTimes =
    totalBlockDiff > 0
      ? hardforks.filter((item) => item.startTimestamp <= nowBlockTime.startTimestamp).reverse()
      : hardforks.filter((item) => item.startTimestamp >= nowBlockTime.startTimestamp);

  const firstAmended = amendedBlockTimes[0];
  if (!firstAmended) {
    return new Date(estimatedTargetTime);
  }

  let ret: number;

  if (totalBlockDiff > 0) {
    const { timestamp } = amendedBlockTimes.reduce(
      (accu, curr) => {
        if (accu.remainingBlockCount <= 0) return accu;

        const blockDiff = (accu.timestamp - curr.startTimestamp) / curr.blockTimeMs;
        const remainingBlockCount = accu.remainingBlockCount - blockDiff;

        if (remainingBlockCount <= 0) {
          return {
            timestamp: accu.timestamp - accu.remainingBlockCount * curr.blockTimeMs,
            remainingBlockCount,
          };
        }

        // need to traverse more block times
        return {
          timestamp: curr.startTimestamp,
          remainingBlockCount,
        };
      },
      {
        timestamp: now,
        remainingBlockCount: totalBlockDiff,
      },
    );

    ret = timestamp;
  } else {
    const { timestamp } = amendedBlockTimes.reduce(
      (accu, curr, index) => {
        if (accu.remainingBlockCount >= 0 || index === 0) return accu;

        const remainingBlockCount =
          accu.remainingBlockCount - (accu.timestamp - curr.startTimestamp) / accu.prevBlockTimeMs;

        // When target falls into the current range.
        if (remainingBlockCount >= 0) {
          return {
            timestamp: accu.timestamp - accu.remainingBlockCount * accu.prevBlockTimeMs,
            remainingBlockCount,
            prevBlockTimeMs: curr.blockTimeMs,
          };
        }

        return {
          timestamp:
            curr.startTimestamp -
            (index === amendedBlockTimes.length - 1 && remainingBlockCount < 0
              ? remainingBlockCount * curr.blockTimeMs
              : 0),
          remainingBlockCount,
          prevBlockTimeMs: curr.blockTimeMs,
        };
      },
      {
        remainingBlockCount: totalBlockDiff,
        timestamp: now,
        prevBlockTimeMs: firstAmended.blockTimeMs,
      },
    );

    ret = timestamp;
  }

  return ret > 0 ? new Date(ret) : undefined;
};
